import React, { useState } from "react";
import { Fade } from "react-awesome-reveal";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "elements/Button";

export const DiscussForm = () => {
  const [formData, setFormData] = useState({
    expectations: "",
    motivation: "",
    dreamGoals: "",
    challenges: [],
    preferredSupport: "",
    name: "",
    age: "",
    location: "",
    treatmentDuration: "",
    treatmentCompleted: "",
    email: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const questions = [
    {
      id: "expectations",
      label: "Wat zou je verwachten van de RevOnc app?",
      type: "textarea",
      placeholder: "Beschrijf hier je verwachtingen",
    },
    {
      id: "motivation",
      label: "Wat heeft je gemotiveerd om in te schrijven op de wachtlijst?",
      type: "textarea",
      placeholder: "Wat is je motivatie?",
    },
    {
      id: "dreamGoals",
      label: "Wat is de droom die jij zou willen bereiken met de RevOnc app?",
      type: "textarea",
      placeholder: "Beschrijf je doelen",
    },
    {
      id: "challenges",
      label: "Wat zijn je grootste uitdagingen?",
      type: "checkbox",
      options: ["Mentaal welzijn", "Fysieke gezondheid", "Sociale re-integratie", "Werkhervatting"],
    },
    {
      id: "preferredSupport",
      label: "Hoe zou je het liefst ondersteund worden?",
      type: "radio",
      options: [
        "KickStart+: Gepersonaliseerde coaching via de app",
        "Feedback+: Gepersonaliseerde coaching + online coaching",
        "Premium+: Gepersonaliseerde coaching + in persoon coaching",
        "Connect+: Gepersonaliseerde coaching + groepslessen",
      ],
    },
    {
      id: "name",
      label: "Wat is je naam?",
      type: "text",
      placeholder: "Vul je naam in",
    },
    {
      id: "age",
      label: "Wat is je leeftijd?",
      type: "number",
      placeholder: "Vul je leeftijd in",
    },
    {
      id: "location",
      label: "Wat is je locatie?",
      type: "text",
      placeholder: "Locatie",
    },
    {
      id: "treatmentDuration",
      label: "Hoelang ben je al in behandeling?",
      type: "text",
      placeholder: "Duur van de behandeling",
    },
    {
      id: "treatmentCompleted",
      label: "Is je behandeling afgerond?",
      type: "radio",
      options: ["Ja", "Nee"],
    },
    {
      id: "email",
      label: "Laat je e-mailadres achter om op de hoogte gehouden te worden",
      type: "email",
      placeholder: "Vul je e-mailadres in",
    },
  ];

  const questionsPerPage = 2;
  const totalSteps = Math.ceil(questions.length / questionsPerPage);

  const handleInputChange = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleSubmit = () => {
    // Debug: Check formData before sending

    // Prepare the data to match EmailJS template variables
    const emailData = {
      name: formData.name,
      email: formData.email,
      age: formData.age,
      location: formData.location,
      treatmentDuration: formData.treatmentDuration,
      treatmentCompleted: formData.treatmentCompleted,
      expectations: formData.expectations,
      motivation: formData.motivation,
      dreamGoals: formData.dreamGoals,
      challenges: formData.challenges.join(", "), // Convert array to string
      preferredSupport: formData.preferredSupport,
    };

    emailjs
      .send("service_zcwsbym", "template_s18qj28", emailData, "ddWh2pAB2XmyXzuQN")
      .then(() => {
        setSubmitted(true);
        toast.success("Formulier succesvol verzonden!");
      })
      .catch((error) => {
        console.error("EmailJS Error:", error); // eslint-disable-line no-console
        toast.error(`Er is een fout opgetreden: ${error.message}`);
      });
  };

  const renderQuestion = (question) => {
    switch (question.type) {
      case "text":
      case "number":
      case "email":
      case "textarea":
        return (
          <input
            id={question.id}
            type={question.type}
            value={formData[question.id] || ""}
            placeholder={question.placeholder}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="border p-2 rounded w-full"
          />
        );
      case "radio":
        return question.options.map((option, index) => {
          const inputId = `${question.id}-${index}`;
          return (
            <div key={option} className="mb-2">
              <input
                id={inputId}
                type="radio"
                name={question.id}
                value={option}
                checked={formData[question.id] === option}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className="mr-2"
              />
              <label htmlFor={inputId} className="inline-block">
                {option}
              </label>
            </div>
          );
        });
      case "checkbox":
        return question.options.map((option, index) => {
          const inputId = `${question.id}-${index}`;
          return (
            <div key={option} className="mb-2">
              <input
                id={inputId}
                type="checkbox"
                value={option}
                checked={formData[question.id]?.includes(option) || false}
                onChange={(e) => {
                  const { value, checked } = e.target;
                  setFormData((prevData) => {
                    const currentValues = prevData[question.id] || [];
                    return {
                      ...prevData,
                      [question.id]: checked
                        ? [...currentValues, value]
                        : currentValues.filter((v) => v !== value),
                    };
                  });
                }}
                className="mr-2"
              />
              <label htmlFor={inputId} className="inline-block">
                {option}
              </label>
            </div>
          );
        });
      default:
        return null;
    }
  };

  return (
    <section className="container mx-auto mt-10 p-6">
      <Fade direction="down" triggerOnce>
        <h1 className="text-3xl text-center font-bold">RevOnc Vragenlijst</h1>
      </Fade>
      <div className="my-6 w-full max-w-lg bg-white p-6 shadow-lg rounded border">
        {submitted ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-green-600">Dankjewel!</h2>
            <p className="mt-4">
              Je formulier is succesvol verzonden. We houden je op de hoogte!
            </p>
          </div>
        ) : (
          <>
            {questions
              .slice(currentStep * questionsPerPage, (currentStep + 1) * questionsPerPage)
              .map((question) => (
                <div key={question.id} className="mb-4">
                  <label className="block font-bold mb-2" htmlFor={question.id}>
                    {question.label}
                  </label>
                  {renderQuestion(question)}
                </div>
              ))}

            {/* Progress Bar */}
            <div className="h-2 bg-gray-200 rounded-full mt-4">
              <div
                className="h-2 bg-theme-purple rounded-full"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>

            <div className="flex justify-between mt-6">
              {currentStep > 0 && (
                <Button
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                >
                  Vorige
                </Button>
              )}
              {currentStep < totalSteps - 1 ? (
                <Button
                  onClick={() => setCurrentStep((prev) => prev + 1)}
                  className="bg-theme-purple text-white px-4 py-2 rounded"
                >
                  Volgende
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-theme-purple text-white px-4 py-2 rounded"
                >
                  Verzenden
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      <ToastContainer />
    </section>
  );
};
